import axios from 'axios';
import qs from 'qs';
import { StrapiResponse, StrapiResponseList } from '../interfaces/commons';
import {
  FidelityResponse,
  FidelityRequest,
  FidelityStatus,
} from '../interfaces/fidelity';

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

export interface RegisterFidelityUserResponse {
  user: {
    id: number;
    email: string;
    username: string;
    confirmed: boolean;
    blocked: boolean;
    createdAt: string;
    updatedAt: string;
    provider: string;
  };
}

export const getFidelities = async ({
  query = {},
  token,
}: {
  query?: any;
  token: string | null;
}) => {
  const { data } = await axios.get<StrapiResponseList<FidelityResponse>>(
    `${REACT_APP_API_URL}/fidelities?${qs.stringify(query, {
      encodeValuesOnly: true,
    })}`,
    {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    }
  );
  return data;
};

export const getFidelity = async ({
  id,
  token,
}: {
  id: string | number;
  token: string | null;
}) => {
  const query = {
    populate: {
      sezione: '*',
      user: '*',
      categorie: '*',
      immagini: '*',
    },
  };

  const { data } = await axios.get<StrapiResponse<FidelityResponse>>(
    `${REACT_APP_API_URL}/fidelities/${id}?${qs.stringify(query)}`,
    {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    }
  );
  return data;
};

export const createFidelity = async ({
  body,
  token,
}: {
  body: FidelityRequest;
  token: string | null;
}) => {
  const { data } = await axios.post<StrapiResponse<FidelityResponse>>(
    `${REACT_APP_API_URL}/fidelities`,
    { data: body },
    {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    }
  );
  return data;
};

export const updateFidelity = async ({
  id,
  body,
  token,
}: {
  id: string | number;
  body: Partial<FidelityRequest>;
  token: string | null;
}) => {
  const sanitized: any = {};
  Object.keys(body || {}).forEach((k) => {
    const val = (body as any)[k];
    if (val !== undefined) sanitized[k] = val;
  });
  const { data } = await axios.put<StrapiResponse<FidelityResponse>>(
    `${REACT_APP_API_URL}/fidelities/${id}`,
    { data: sanitized },
    {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    }
  );
  return data;
};

export const deleteFidelity = async ({
  id,
  token,
}: {
  id: string | number;
  token: string | null;
}) => {
  const { data } = await axios.delete(`${REACT_APP_API_URL}/fidelities/${id}`, {
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  return data;
};

// Istanza dedicata: l'interceptor globale (contexts/addErrorInterceptor) restituisce
// l'errore come risposta risolta invece di rigettarlo, quindi con axios "nudo" una
// risposta 403/500 arrivava a onSuccess come se fosse il file CSV.
const csvInstance = axios.create();

const getCsvErrorMessage = async (error: unknown) => {
  const fallback = 'Errore durante il download del CSV';
  if (!axios.isAxiosError(error)) return fallback;

  const status = error.response?.status;
  let serverMessage: string | null = null;
  const data = error.response?.data as unknown;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      serverMessage = parsed?.error?.message || null;
    } catch (parseError) {
      serverMessage = null;
    }
  }

  if (status === 401 || status === 403) {
    return `${fallback}: permesso mancante per l'export Fidelity (${status})`;
  }
  if (status) {
    return `${fallback} (${status}${serverMessage ? `: ${serverMessage}` : ''})`;
  }
  return fallback;
};

export const downloadFidelitiesCSV = async ({
  token,
  query = {},
  fileName = 'fidelity-export',
}: {
  token: string | null;
  query?: any;
  fileName?: string;
}) => {
  // L'export riguarda tutti i record filtrati: della query della tabella serve
  // solo `filters` (paginazione, ordinamento e populate li gestisce il backend).
  const exportQuery = query?.filters ? { filters: query.filters } : {};

  let data: Blob;
  try {
    const response = await csvInstance.get<Blob>(
      `${REACT_APP_API_URL}/csv-export/fidelities?${qs.stringify(exportQuery, {
        encodeValuesOnly: true,
      })}`,
      {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
        responseType: 'blob',
      }
    );
    data = response.data;
  } catch (error) {
    throw new Error(await getCsvErrorMessage(error));
  }

  const link = document.createElement('a');
  link.href = URL.createObjectURL(data);
  link.download = `${fileName}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 7000);
};

export const approveFidelity = async ({
  id,
  token,
}: {
  id: string | number;
  token: string | null;
}) => {
  return updateFidelity({
    id,
    body: { stato: FidelityStatus.APPROVATO },
    token,
  });
};

export const rejectFidelity = async ({
  id,
  token,
  note,
}: {
  id: string | number;
  token: string | null;
  note: string;
}) => {
  return updateFidelity({
    id,
    body: { stato: FidelityStatus.RIFIUTATO, revisionNote: note },
    token,
  });
};

export const validateFidelityCard = async ({
  token,
  numeroTessera,
}: {
  token: string | null;
  numeroTessera: number;
}) => {
  const { data } = await axios.post(
    `${REACT_APP_API_URL}/fidelity/validate-card`,
    { numeroTessera },
    { headers: { Authorization: token ? `Bearer ${token}` : '' } }
  );
  return data as { valid: boolean; socioOnav: boolean; message: string | null };
};

export const getFidelityCategories = async () => {
  const { data } = await axios.get(`${REACT_APP_API_URL}/fidelity/categories`, {
    headers: {},
  });
  return data as {
    data: Array<{ category: string; subcategories: string[] }>;
  };
};

export const registerFidelityAccount = async ({
  email,
  password,
  piva,
  userSlug,
}: {
  email: string;
  password: string;
  piva: string;
  userSlug?: string;
}) => {
  const payload: Record<string, any> = {
    isFidelity: true,
    email,
    password,
    piva,
  };
  if (userSlug) payload.userSlug = userSlug;
  const response = await axios.post<RegisterFidelityUserResponse>(
    `${REACT_APP_API_URL}/auth/local/register`,
    payload,
    {
      headers: {},
    }
  );

  if (axios.isAxiosError(response)) {
    throw new Error(
      (response.response?.data as { error: { message: string } })?.error
        ?.message || 'Errore durante la registrazione Fidelity'
    );
  }

  return response.data;
};

export const findFidelityByUserId = async ({
  userId,
  token,
}: {
  userId: number;
  token: string | null;
}) => {
  const query = {
    filters: { user: { id: { $eq: userId } } },
    populate: { sezione: '*', user: '*', categorie: '*', immagini: '*' },
    pagination: { limit: 1 },
  };
  const { data } = await axios.get<StrapiResponseList<FidelityResponse>>(
    `${REACT_APP_API_URL}/fidelities?${qs.stringify(query, {
      encodeValuesOnly: true,
    })}`,
    {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    }
  );
  return data;
};
